import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaPhone, FaMapMarkerAlt, FaEnvelope, FaFacebookF, FaWhatsapp, FaInstagram, FaTiktok } from 'react-icons/fa';
import logo from '../assets/Navbar/logo.png';

const getFlagEmoji = (countryCode) => {
    if (!countryCode) return '';
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map((char) => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
};

const Navbar = () => {
    const languages = [
        { code: 'en', name: 'English', country: 'GB' },   // United Kingdom flag for English
        { code: 'de', name: 'German', country: 'DE' },
        { code: 'ru', name: 'Russian', country: 'RU' },
        { code: 'pl', name: 'Polish', country: 'PL' },
        { code: 'it', name: 'Italian', country: 'IT' },
        { code: 'zh', name: 'Chinese', country: 'CN' },   // Simplified Chinese → China flag
    ];

    const { t, i18n } = useTranslation();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };

    // === NAVIGATION ITEMS (tradotti) ===
    const navItems = [
        { to: '/', label: t('home') },
        { to: '/about-us', label: t('aboutUs') },
        { to: '/srilanka', label: t('sriLankanav') },
        { to: '/gallery', label: t('gallerynav') },
        { to: '/tour', label: t('bookATour') },
        { to: '/things', label: t('thingsToDo') },
        { to: '/contact-us', label: t('contactUs') },
    ];

    return (
        <div className="relative">
            {/* Top Section */}
            <div
                className={`flex items-center justify-between text-white ${isScrolled ? 'shadow-sm' : ''
                    } bg-white border-b-4  px-4 sm:px-6 lg:px-8 relative h-40 sm:h-48 md:h-48 transition-all duration-300`}
            >
                {/* Centered Logo */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <img
                        src={logo}
                        alt="Ceylon Tours"
                        className="h-48 sm:h-48 md:h-48 lg:h-48 w-auto object-contain"
                    />
                </div>

                {/* Language Selector and Social Icons (Desktop) */}
                <div className="hidden md:flex flex-col gap-3 absolute top-4 sm:top-6 md:top-8 lg:top-10 right-4 sm:right-6">
                    {/* Language Dropdown - Solo IT/EN */}
                    <select
                        onChange={(e) => changeLanguage(e.target.value)}
                        value={i18n.language}
                        className="lg:ml-32 bg-white text-[#2E4B2C] border border-[#4A7043] rounded-lg px-3 py-1.5 sm:px-4 sm:py-2 text-sm sm:text-base w-20 sm:w-36 shadow-sm hover:bg-gray-100 hover:text-[#4A7043] transition-all focus:outline-none focus:ring-2 focus:ring-[#4A7043]"
                        aria-label="Select language"
                    >
                        {languages.map((lang) => (
                            <option key={lang.code} value={lang.code}>
                                {getFlagEmoji(lang.country)} {lang.name}
                            </option>
                        ))}
                    </select>

                    {/* Social Icons */}
                    <div className="flex gap-3 sm:gap-4 sm:mt-8 md:mt-16">
                        <a href="tel:+94768051224" aria-label="Chiamaci">
                            <FaPhone className="w-5 h-5 sm:w-6 sm:h-6 text-[#009CE6] hover:text-[#4A7043] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://maps.app.goo.gl/M77TvQEWCftGEx3G9"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Vedi posizione"
                        >
                            <FaMapMarkerAlt className="w-5 h-5 sm:w-6 sm:h-6 text-[#009CE6] hover:text-[#4A7043] transition-transform transform hover:scale-110" />
                        </a>
                        <a href="mailto:info@starlankatours.com" aria-label="Scrivici">
                            <FaEnvelope className="w-5 h-5 sm:w-6 sm:h-6 text-[#009CE6] hover:text-[#4A7043] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://www.facebook.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visita la nostra pagina Facebook"
                        >
                            <FaFacebookF className="w-5 h-5 sm:w-6 sm:h-6 text-[#009CE6] hover:text-[#4A7043] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://wa.me/+94768051224"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Contattaci su WhatsApp"
                        >
                            <FaWhatsapp className="w-5 h-5 sm:w-6 sm:h-6 text-[#009CE6] hover:text-[#4A7043] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://www.instagram.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visita il nostro Instagram"
                        >
                            <FaInstagram className="w-5 h-5 sm:w-6 sm:h-6 text-[#009CE6] hover:text-[#4A7043] transition-transform transform hover:scale-110" />
                        </a>
                        <a
                            href="https://www.tiktok.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Visita il nostro TikTok"
                        >
                            <FaTiktok className="w-5 h-5 sm:w-6 sm:h-6 text-[#009CE6] hover:text-[#4A7043] transition-transform transform hover:scale-110" />
                        </a>
                    </div>
                </div>

                {/* Language Selector and Menu Button (Mobile) */}
                <div className="md:hidden flex flex-col gap-3 absolute top-4 right-4">
                    {/* Language Dropdown - Solo IT/EN */}


                    {/* Menu Button */}
                    <button
                        className="text-[#009CE6] bg-transparent border-none mt-10 ml-20"
                        onClick={toggleMenu}
                        aria-label="Apri menu mobile"
                    >
                        <svg
                            className="w-7 h-7 sm:w-8 sm:h-8"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M4 6h16M4 12h16m-7 6h7"
                            ></path>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Bottom Section (Desktop Navigation) */}
            <div
                className="hidden md:flex justify-center items-center px-4 sm:px-6 lg:px-8 py-2 bg-[#009CE6] sticky top-0 z-50"
                style={{ boxShadow: isScrolled ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none' }}
            >
                <ul className="flex flex-wrap justify-center gap-2 sm:gap-4 lg:gap-6 mb-0 font-serif text-lg sm:text-xl lg:text-2xl">
                    {navItems.map((item) => (
                        <li key={item.to} className="nav-item">
                            <NavLink
                                to={item.to}
                                className={({ isActive }) =>
                                    `nav-link px-3 py-2 sm:px-4 sm:py-2 font-copperplate text-white hover:text-white transition-colors rounded-md ${isActive ? 'underline bg-[#96BA33] text-white font-bold font-copperplate' : ''
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Mobile Menu */}
            {isMenuOpen && (
                <div
                    className="md:hidden fixed top-0 right-0 h-full w-3/4 sm:w-2/3 max-w-xs bg-[#009CE6] shadow-2xl border-l border-gray-200 z-50 p-4 sm:p-5 rounded-l-lg animate-slideIn"
                    style={{ fontFamily: 'Lato, sans-serif' }}
                >
                    <style>
                        {`
              @keyframes slideIn {
                from {
                  transform: translateX(100%);
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
            `}
                    </style>

                    {/* Close Button */}
                    <button
                        className="absolute top-4 right-4 bg-yellow-100 text-yellow-800 border border-yellow-800 rounded-full w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center text-xl sm:text-2xl font-bold transition-all hover:bg-yellow-200 hover:scale-110"
                        onClick={toggleMenu}
                        aria-label="Chiudi menu mobile"
                    >
                        X
                    </button>
                    <select
                        onChange={(e) => changeLanguage(e.target.value)}
                        value={i18n.language}
                        className="bg-white text-[#2E4B2C] border border-[#4A7043] rounded-lg py-1.5 text-sm w-32 shadow-sm hover:bg-gray-100 hover:text-[#4A7043] transition-all focus:outline-none focus:ring-2 focus:ring-[#4A7043] mt-20"
                        aria-label="Select language"
                    >
                        {languages.map((lang) => (
                            <option key={lang.code} value={lang.code}>
                                {getFlagEmoji(lang.country)} {lang.name}
                            </option>
                        ))}
                    </select>

                    {/* Navigation Links */}
                    <ul className="flex flex-col mt-16 gap-2">
                        {navItems.map((item) => (
                            <li key={item.to} className="nav-item">
                                <NavLink
                                    to={item.to}
                                    onClick={toggleMenu}
                                    className={({ isActive }) =>
                                        `font-copperplate nav-link text-white hover:bg-[#96BA33] hover:text-white transition-all  text-lg sm:text-xl py-2 px-4 block rounded-md ${isActive ? 'underline bg-[#96BA33] font-bold' : ''
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>

                    {/* Social Icons (Mobile) */}
                    <div className="flex gap-3 sm:gap-4 mt-4 justify-center border-t border-gray-200 pt-4">
                        <a href="tel:+94768051224" aria-label="Chiamaci" className="text-[#000000] hover:text-[#96BA33] transition-transform transform hover:scale-110">
                            <FaPhone className="w-5 h-5 sm:w-6 sm:h-6" />
                        </a>
                        <a href="https://maps.app.goo.gl/M77TvQEWCftGEx3G9" target="_blank" rel="noopener noreferrer" aria-label="Vedi posizione" className="text-[#000000] hover:text-[#96BA33] transition-transform transform hover:scale-110">
                            <FaMapMarkerAlt className="w-5 h-5 sm:w-6 sm:h-6" />
                        </a>
                        <a href="mailto:info@starlankatours.com" aria-label="Scrivici" className="text-[#000000] hover:text-[#96BA33] transition-transform transform hover:scale-110">
                            <FaEnvelope className="w-5 h-5 sm:w-6 sm:h-6" />
                        </a>
                        <a href="https://www.facebook.com/" target="_blank" rel="noopener noreferrer" aria-label="Visita Facebook" className="text-[#000000] hover:text-[#96BA33] transition-transform transform hover:scale-110">
                            <FaFacebookF className="w-5 h-5 sm:w-6 sm:h-6" />
                        </a>
                        <a href="https://wa.me/+94768051224" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="text-[#000000] hover:text-[#96BA33] transition-transform transform hover:scale-110">
                            <FaWhatsapp className="w-5 h-5 sm:w-6 sm:h-6" />
                        </a>
                        <a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer" aria-label="Visita Instagram" className="text-[#000000] hover:text-[#96BA33] transition-transform transform hover:scale-110">
                            <FaInstagram className="w-5 h-5 sm:w-6 sm:h-6" />
                        </a>
                        <a href="https://www.tiktok.com/" target="_blank" rel="noopener noreferrer" aria-label="Visita TikTok" className="text-[#000000] hover:text-[#96BA33] transition-transform transform hover:scale-110">
                            <FaTiktok className="w-5 h-5 sm:w-6 sm:h-6" />
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Navbar;